// Object utils

/**
 * Clones an object, making a snapshot
 * @param o Object to clone
 * @returns The cloned object
 */
export function clone<T = any>(o: T): T {
    if (typeof o !== "object" || o === null) {
        return o; // Primitives don't need cloning
    }

    if (o instanceof Set) {
        const o2 = new Set();

        for (const e of o) {
            o2.add(clone(e));
        }

        return <any>o2;
    } else if (o instanceof Map) {
        const o2 = new Map();

        for (const [k, v] of o) {
            o2.set(k, clone(v));
        }

        return <any>o2;
    } else if (Array.isArray(o)) {
        return <any>o.map(clone);
    } else {
        const o2: T = Object.create(null);

        for (const key of Object.keys(o)) {
            o2[key] = clone(o[key]);
        }

        return o2;
    }
}

export function prettifyJson(json: string): string {
    try {
        return JSON.stringify(JSON.parse(json), null, 4);
    } catch (ex) {
        return json;
    }
}

/**
 * Checks if 2 objects are equal (deep)
 * @param o1 Object 1
 * @param o2 Object 2
 * @param nullStrict True if undefined and null should be treated as different
 * @returns True if equal
 */
export function deepEqual<T = any>(o1: T, o2: T, nullStrict?: boolean): boolean {
    if (!nullStrict && (o1 === undefined || o1 === null) && (o1 === undefined || o2 === null)) {
        return true;
    }

    if (typeof o1 !== typeof o2) {
        return false;
    }

    if (typeof o1 !== "object" || o1 === null || o2 === null) {
        return o1 === o2;
    }

    if (Array.isArray(o1) && Array.isArray(o2)) {
        if (o1.length !== o2.length) {
            return false;
        }

        for (let i = 0; i < o1.length; i++) {
            if (!deepEqual(o1[i], o2[i], nullStrict)) {
                return false;
            }
        }

        return true;
    } else {
        const propsO1 = Object.keys(o1);

        for (const prop of propsO1) {
            if (!deepEqual(o1[prop], o2[prop], nullStrict)) {
                return false;
            }
        }

        const propsO2 = Object.keys(o2);

        for (const prop of propsO2) {
            if (!deepEqual(o1[prop], o2[prop], nullStrict)) {
                return false;
            }
        }

        return true;
    }
}
